// md-editor-v3 组件的异步加载器
// 组件本体（vendor-md，约 1.8MB）与所需扩展并行加载，不阻塞首屏

import { defineAsyncComponent } from "vue";
import { ensureKatex, ensureMermaid } from "./md-extensions";

/** 预取 md-editor-v3 组件本体与样式，供包装层与加载器并行使用 */
export const preloadMdComponent = () =>
  Promise.all([import("md-editor-v3"), import("md-editor-v3/lib/style.css")]);

// 扩展加载失败时退化为库的默认降级行为（原样显示源码），不阻塞组件渲染
const loadKatex = () =>
  ensureKatex().catch((e) => {
    console.error("Failed to load katex extension:", e);
  });
const loadMermaid = () =>
  ensureMermaid().catch((e) => {
    console.error("Failed to load mermaid extension:", e);
  });

// 【必须在组件创建前注入实例】md-editor-v3 在 setup() 阶段就把
// config.editorExtensions.*.instance 快照进 shallowRef，之后不再回读 config，
// 实例缺失时公式/流程图会被原样输出且不会自行恢复，所以扩展要先于组件就绪。
const loadMdComponent = async <T,>(
  pick: (m: typeof import("md-editor-v3")) => T,
  loadExtensions: () => Promise<unknown>
): Promise<T> => {
  const [[m]] = await Promise.all([preloadMdComponent(), loadExtensions()]);
  return pick(m);
};

// 预览：katex 是公式渲染的必需依赖；mermaid 体积接近 2MB 且预览内容只读，
// 改由 MdPreviewLazy 按内容决定是否加载，普通页面因此无需付出这部分开销。
export const MdPreviewAsync = defineAsyncComponent(() =>
  loadMdComponent((m) => m.MdPreview, loadKatex)
);

// 编辑器：内容随时可能被粘贴或输入流程图，实例必须在组件创建时就位才能渲染，
// 因此这里无条件等待 mermaid。它与组件本体大小相当且并行加载，冷启动几乎不增加等待。
export const MdEditorAsync = defineAsyncComponent(() =>
  loadMdComponent((m) => m.MdEditor, () => Promise.all([loadKatex(), loadMermaid()]))
);
